/**
 * Light Airtable-inspired app shell with workspace navigation.
 */

import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isNavItemActive } from '../utils/navigationUi.js';
import { getInitialTheme, getThemeStorageValue, THEME_STORAGE_KEY } from '../utils/themePreference';
import { Button } from './ui.jsx';

const navItems = [
  { path: '/', label: 'Applicants', icon: '👥' },
  { path: '/calendar', label: 'Calendar', icon: '📅' },
  { path: '/needs-attention', label: 'Needs attention', icon: '!' },
  { path: '/settings/import', label: 'Import', icon: '⇪', adminOnly: true },
  { path: '/settings', label: 'Settings', icon: '⚙', adminOnly: true },
];

const pageCopy = {
  '/': { title: 'Applicants', subtitle: 'Track candidates from import through hiring.' },
  '/calendar': { title: 'Calendar', subtitle: 'Schedule interviews and open applicant records.' },
  '/needs-attention': { title: 'Needs attention', subtitle: 'Review applicants where automation needs dispatcher action.' },
  '/settings': { title: 'Settings', subtitle: 'Manage templates, users, and launch configuration.' },
  '/settings/import': { title: 'CSV Import', subtitle: 'Preview candidate rows before adding them to the ATS.' },
};

function cx(...classes) {
  return classes.flat().filter(Boolean).join(' ');
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = getInitialTheme();
  const currentPage = pageCopy[location.pathname] || pageCopy['/'];

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    window.localStorage.setItem(THEME_STORAGE_KEY, getThemeStorageValue(theme));
  }, [theme]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-surface-subtle text-text-primary lg:grid lg:grid-cols-[240px_1fr]">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-lg focus:bg-brand-blue focus:px-4 focus:py-3 focus:text-sm focus:font-semibold focus:text-white focus:outline-none focus:ring-2 focus:ring-brand-blue focus:ring-offset-2"
      >
        Skip to content
      </a>

      <aside className="hidden border-r border-border-default bg-white lg:flex lg:min-h-screen lg:flex-col">
        <div className="border-b border-border-default px-5 py-4">
          <Link to="/" className="flex min-h-[44px] items-center gap-3 rounded-app focus-ring">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-blue text-sm font-bold text-white shadow-sm">FA</span>
            <span>
              <span className="block text-sm font-bold tracking-tight text-text-primary">FACTRANS ATS</span>
              <span className="block text-xs text-text-muted">Hiring workspace</span>
            </span>
          </Link>
        </div>

        <nav className="flex-1 space-y-1 px-3 py-4" aria-label="Primary navigation">
          {navItems.map(item => {
            if (item.adminOnly && user?.role !== 'admin') return null;
            const isActive = isNavItemActive(item.path, location.pathname);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={isActive ? 'page' : undefined}
                className={cx(
                  'group flex min-h-[40px] items-center gap-3 rounded-app border px-3 py-2 text-sm font-semibold transition-colors focus-ring',
                  isActive
                    ? 'border-info-border bg-brand-blueSoft text-brand-blue'
                    : 'border-transparent text-text-secondary hover:bg-surface-hover hover:text-text-primary',
                )}
              >
                <span aria-hidden="true" className="w-5 text-center text-base">{item.icon}</span>
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-border-default p-4">
          <div className="mb-3 flex items-center gap-3 rounded-card border border-border-default bg-surface-subtle p-3">
            <UserInitials name={user?.name} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-text-primary">{user?.name || 'User'}</p>
              <p className="text-xs capitalize text-text-muted">{user?.role || 'member'}</p>
            </div>
          </div>
          <Button type="button" variant="secondary" size="sm" className="w-full" onClick={handleLogout}>
            Logout
          </Button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-40 border-b border-border-default bg-white/95 backdrop-blur">
          <div className="flex min-h-14 flex-col gap-3 px-4 py-3 sm:px-5 lg:flex-row lg:items-center lg:justify-between lg:px-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-brand-blue">FACTRANS ATS</p>
              <h1 className="text-[22px] font-bold leading-7 tracking-[-0.02em] text-text-primary sm:text-[28px] sm:leading-9">{currentPage.title}</h1>
              <p className="text-sm text-text-muted">{currentPage.subtitle}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {user?.role === 'admin' && location.pathname !== '/settings/import' && (
                <Button as={Link} to="/settings/import" variant="primary" size="sm">Import CSV</Button>
              )}
              {location.pathname === '/' && (
                <Button as="a" href="/apply" target="_blank" rel="noreferrer" variant="secondary" size="sm">Application Form ↗</Button>
              )}
            </div>
          </div>

          <nav className="flex gap-2 overflow-x-auto border-t border-border-default px-4 py-2 no-print lg:hidden" aria-label="Primary navigation">
            {navItems.map(item => {
              if (item.adminOnly && user?.role !== 'admin') return null;
              const isActive = isNavItemActive(item.path, location.pathname);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  aria-current={isActive ? 'page' : undefined}
                  className={cx(
                    'touch-target inline-flex flex-shrink-0 items-center gap-2 rounded-app border px-3 py-2 text-sm font-semibold focus-ring',
                    isActive
                      ? 'border-info-border bg-brand-blueSoft text-brand-blue'
                      : 'border-border-default bg-white text-text-secondary hover:bg-surface-hover',
                  )}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  {item.label}
                </Link>
              );
            })}
            <button
              type="button"
              onClick={handleLogout}
              className="touch-target inline-flex flex-shrink-0 items-center rounded-app border border-border-default bg-white px-3 py-2 text-sm font-semibold text-text-secondary hover:bg-surface-hover focus-ring"
            >
              Logout
            </button>
          </nav>
        </header>

        <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
          {children}
        </main>
      </div>
    </div>
  );
}

function getInitials(name = '') {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase())
    .join('') || '?';
}

function UserInitials({ name }) {
  return (
    <span
      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-blueSoft text-sm font-bold text-brand-blue ring-1 ring-info-border"
      title={name || 'Current user'}
      aria-label={name ? `Signed in as ${name}` : 'Signed in user'}
    >
      {getInitials(name)}
    </span>
  );
}
